import React, { useEffect, useState } from 'react';
import { apiGet } from '../lib/api';
import { Badge } from '../lib/badge';
import Icon from '../components/icons';

export default function Reports(){
  const [credits, setCredits] = useState({ by_day:[], total:0 });
  const [adoption, setAdoption] = useState({ suites:[], overlays:[] });
  const [agents, setAgents] = useState({ byAgent:[] });
  const [gov, setGov] = useState({});
  const [billing, setBilling] = useState({ monthly:[] });
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    Promise.all([
      apiGet('/reports/credits/summary'),
      apiGet('/reports/adoption'),
      apiGet('/reports/agents/utilization'),
      apiGet('/reports/governance'),
      apiGet('/reports/billing'),
      apiGet('/dashboard/summary'),
    ]).then(([c,a,ag,g,b,s])=>{ setCredits(c); setAdoption(a); setAgents(ag); setGov(g); setBilling(b); setSummary(s); })
      .catch(e=>setErr(String(e)))
      .finally(() => setLoading(false));
  },[]);

  const snapshotCards = summary ? [
    { label: 'Skills', value: summary.totals?.skills },
    { label: 'Active Skills', value: summary.totals?.active_skills },
    { label: 'Runs', value: summary.totals?.runs },
    { label: 'Pending Approvals', value: summary.totals?.pending_approvals },
    { label: 'Integrations', value: summary.totals?.integrations },
  ] : [];

  return (
    <main className="page">
      <div className="page__header">
        <span className="page__icon"><Icon name="reports" /></span>
        <div className="page__header-text">
          <h2>Reporting &amp; Analytics</h2>
          <p>Credits, adoption, agent utilization, governance, and billing — rolled up from live data.</p>
        </div>
      </div>
      {err && <div className="status status--error">{err}</div>}
      {loading ? (
        <p className="status status--muted">Loading reports…</p>
      ) : (
        <>
          {summary && (
            <section className="stats-grid stats-grid--compact">
              {snapshotCards.map((c) => (
                <div className="stat-card" key={c.label}>
                  <h3>{c.label}</h3>
                  <p>{c.value ?? 0}</p>
                </div>
              ))}
            </section>
          )}

          <section className="panel-grid">
            <article className="panel">
              <h3>Skills by Lifecycle</h3>
              <ul className="simple-list">
                {(summary?.skills_by_lifecycle || []).map((r) => (
                  <li key={r.lifecycle}>
                    <Badge value={r.lifecycle} />
                    <strong>{r.count}</strong>
                  </li>
                ))}
              </ul>
            </article>

            <article className="panel">
              <h3>Governance (Approvals)</h3>
              <ul className="simple-list">
                <li><span>Required</span><strong>{gov.approval_required || 0}</strong></li>
                <li><span>Granted</span><strong>{gov.approval_granted || 0}</strong></li>
                <li><span>Denied</span><strong>{gov.approval_denied || 0}</strong></li>
              </ul>
            </article>
          </section>

          <section className="panel">
            <h3>Credit Consumption <span className="panel__hint">(30 days)</span></h3>
            <p className="panel__stat">Total: <strong>{credits.total}</strong> credits</p>
            <ul className="simple-list simple-list--scroll">
              {credits.by_day.map((d,i)=> <li key={i}><span>{d.day}</span><strong>{d.credits}</strong></li>)}
              {credits.by_day.length === 0 && <li className="cell-muted">No usage recorded yet.</li>}
            </ul>
          </section>

          <section className="panel-grid">
            <article className="panel">
              <h3>Top Suites</h3>
              <ul className="simple-list">
                {adoption.suites.map((s,i)=>(
                  <li key={i}><span>Suite {s.suite_id}</span><strong>{s.workspaces} workspaces</strong></li>
                ))}
                {adoption.suites.length === 0 && <li className="cell-muted">No adoption data yet.</li>}
              </ul>
            </article>
            <article className="panel">
              <h3>Top Overlays</h3>
              <ul className="simple-list">
                {adoption.overlays.map((o,i)=>(
                  <li key={i}><span>Overlay {o.overlay_id}</span><strong>{o.workspaces} workspaces</strong></li>
                ))}
                {adoption.overlays.length === 0 && <li className="cell-muted">No adoption data yet.</li>}
              </ul>
            </article>
          </section>

          <section className="panel">
            <h3>Agent Utilization</h3>
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Agent</th><th>Runs</th><th>Success rate</th></tr></thead>
                <tbody>
                  {agents.byAgent.map((a,i)=>(
                    <tr key={i}>
                      <td className="cell-strong">{a.agent_id}</td>
                      <td>{a.runs}</td>
                      <td>{a.success_rate}</td>
                    </tr>
                  ))}
                  {agents.byAgent.length === 0 && (
                    <tr><td colSpan={3} className="cell-muted">No runs recorded yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel">
            <h3>Billing <span className="panel__hint">(monthly usage)</span></h3>
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Month</th><th>Workspace</th><th>Customer</th><th>Credits</th></tr></thead>
                <tbody>
                  {billing.monthly.map((m,i)=>(
                    <tr key={i}>
                      <td>{m.month}</td>
                      <td className="cell-muted">ws-{m.workspace_id}</td>
                      <td className="cell-muted">cust-{m.customer_id}</td>
                      <td className="cell-strong">{m.credits}</td>
                    </tr>
                  ))}
                  {billing.monthly.length === 0 && (
                    <tr><td colSpan={4} className="cell-muted">No billing data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </main>
  );
}
